/**
 * Shortest-path interdiction as a single mixed-integer program in CPLEX LP
 * format (the format HiGHS reads), the exact engine behind the Labyrinth
 * Keeper reveal.
 *
 * The trick is LP duality. The hero's shortest path from s to t equals the
 * optimum of the dual of the shortest-path LP:
 *
 *     maximize   π_t − π_s
 *     subject to π_v − π_u ≤ c_uv     for every available arc (u,v)
 *
 * The keeper *removes* cells; a removed arc simply drops its constraint, so its
 * π-gap is free and the shortest path can only grow. Removing is a binary z, and
 * relaxing the constraint is the standard big-M:
 *
 *     maximize   π_t − π_s
 *     s.t.       π_v − π_u ≤ c_uv + M·z_a     (interdictable arc a)
 *                Σ z_a ≤ B,  z_a ∈ {0,1}
 *
 * Both the keeper (choose z) and the hero-dual (choose π) maximize, so the whole
 * thing is a single maximization — one MILP, solved to a *proven* optimum.
 *
 * Cells, not edges, are walled, so each passable cell is split into two nodes
 * with an internal arc in→out; walling the cell removes that one arc (binary
 * z_cell). Movement arcs between adjacent cells cost 1 step and are never
 * interdicted. Start and goal cells are uncuttable.
 */

import type { GridCell, InterdictionRequest } from "./interdiction";
import { cellId, passableCells } from "./interdiction";

export interface InterdictionModel {
  lp: string;
  /** Map z-variable cell-id → [row, col], to decode the chosen walls. */
  cellOf: Map<number, GridCell>;
  /** Big-M used (an upper bound on any shortest-path length). */
  M: number;
}

const key = (r: number, c: number) => r + "," + c;

/**
 * Build the interdiction MILP. Node ids: cell `i` (flattened id) → in-node `2i`,
 * out-node `2i+1`. Source = start_in, sink = goal_out. π variables are
 * `p_<node>`; wall variables are `z_<cellId>`.
 */
export function buildInterdictionLP(req: InterdictionRequest): InterdictionModel {
  const { rows, cols } = req;
  const cells = passableCells(req);
  const passable = new Set(cells.map(([r, c]) => key(r, c)));
  const inNode = (r: number, c: number) => 2 * cellId(r, c, cols);
  const outNode = (r: number, c: number) => 2 * cellId(r, c, cols) + 1;

  // M = an upper bound on the longest possible shortest path (a simple path
  // visits each passable cell at most once).
  const M = cells.length + 1;

  const startKey = key(...req.start);
  const goalKey = key(...req.goal);
  const source = inNode(...req.start);
  const sink = outNode(...req.goal);

  const cellOf = new Map<number, GridCell>();

  let lp = "\\ Shortest-path interdiction - dual-potential MILP (HiGHS)\n";
  lp += `\\ grid ${rows}x${cols}, ${cells.length} passable cells, budget ${req.budget}\n`;
  lp += "Maximize\n";
  lp += ` obj: p_${sink} - p_${source}\n`;
  lp += "Subject To\n";

  // Internal arcs (in → out, cost 0). Terminal cells are uncuttable.
  const zVars: string[] = [];
  for (const [r, c] of cells) {
    const cid = cellId(r, c, cols);
    const inN = inNode(r, c);
    const outN = outNode(r, c);
    if (key(r, c) === startKey || key(r, c) === goalKey) {
      lp += ` int_${cid}: p_${outN} - p_${inN} <= 0\n`;
    } else {
      // p_out - p_in - M z <= 0   ⇒ z=0 keeps cell passable, z=1 frees the gap
      lp += ` int_${cid}: p_${outN} - p_${inN} - ${M} z_${cid} <= 0\n`;
      zVars.push(`z_${cid}`);
      cellOf.set(cid, [r, c]);
    }
  }

  // Movement arcs (out_u → in_v, cost 1 step) for each adjacent passable pair.
  const DIRS: GridCell[] = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  let mv = 0;
  for (const [r, c] of cells) {
    for (const [dr, dc] of DIRS) {
      const nr = r + dr;
      const nc = c + dc;
      if (!passable.has(key(nr, nc))) continue;
      lp += ` mv_${mv++}: p_${inNode(nr, nc)} - p_${outNode(r, c)} <= 1\n`;
    }
  }

  // Budget: at most B walls.
  lp += ` budget: ${zVars.join(" + ")} <= ${req.budget}\n`;

  // π ∈ [0, M]; anchor the source at 0. (Distances from the source are ≥ 0 and
  // never exceed M, so this is exact and keeps the solve well-scaled.)
  lp += "Bounds\n";
  for (const [r, c] of cells) {
    for (const node of [inNode(r, c), outNode(r, c)]) {
      // Anchor the source potential at exactly 0; all others in [0, M].
      lp += node === source ? ` p_${node} = 0\n` : ` 0 <= p_${node} <= ${M}\n`;
    }
  }

  lp += "Binary\n";
  for (const z of zVars) lp += ` ${z}\n`;
  lp += "End\n";

  return { lp, cellOf, M };
}
